// Shared inline-Markdown composer used by both the file-upload and
// manual-entry adapters. Wraps the existing core-engine Markdown
// exporter so React components don't re-implement any formatting
// logic. Pure (no DOM); safe to import from the browser bundle.

import { formatNewSupplyResult, formatChangePremiumResult, formatExcessGfaResult, formatTemporaryUseResult, formatLeaseRentResult, formatCapacityIncreaseResult } from "../../export/markdown.js";
import type { Parcel } from "../../types/index.js";
import type { ChangePremiumResult } from "../../types/change-premium.js";
import type { ExcessGfaResult } from "../../logic/excess-gfa.js";
import type { TemporaryUseFeeResult } from "../../logic/temporary-use.js";
import type { KeyIndustrialLeaseAnnualRentResult } from "../../types/lease-rent.js";
import type { IndustrialCapacityIncreaseResult } from "../../types/capacity-increase.js";

import type { RunResult } from "./runFile.js";

export function buildInlineMarkdown(out: {
  readonly result: RunResult;
}): string {
  const r = out.result;

  switch (r.calculationTypeRaw) {
    case "new-supply":
      if (r.itemResults && r.marketPrices) {
        return formatNewSupplyResult(
          {
            projectName: r.projectName ?? "—",
            parcelId: undefined,
            landArea: 0,
            supplyMethod: "招拍挂",
            calculationType: "新供应土地",
            calculationDate: r.calculationDate ?? "—",
            mainLandUse: "住宅",
            projectType: "普通项目",
            totalGfa: r.totalChargeableGfa ?? 0,
          } as unknown as Parcel,
          r.marketPrices,
          {
            itemResults: r.itemResults as never,
            totalLandPrice: r.totalLandPrice ?? 0,
            totalChargeableGfa: r.totalChargeableGfa ?? 0,
            averageFloorLandPrice: r.averageFloorLandPrice ?? 0,
            validationMessages: [...r.validationMessages],
          },
        );
      }
      break;
    case "change-premium":
      if (r.beforeResult && r.afterResult) {
        const cp: ChangePremiumResult = {
          beforeResult: r.beforeResult,
          afterResult: r.afterResult,
          difference:
            r.afterResult.totalLandPrice - r.beforeResult.totalLandPrice,
          premium: r.premium ?? 0,
        };
        return formatChangePremiumResult(cp, r.projectName);
      }
      break;
    case "excess-gfa":
      if (r.beforeResult && r.afterResult) {
        const eg: ExcessGfaResult = {
          base: {
            beforeResult: r.beforeResult,
            afterResult: r.afterResult,
            difference:
              r.afterResult.totalLandPrice - r.beforeResult.totalLandPrice,
            premium:
              (r.afterResult.totalLandPrice - r.beforeResult.totalLandPrice) *
              1.3,
          },
          excessGfaPremium: r.premium ?? 0,
          note: r.note,
        };
        return formatExcessGfaResult(eg);
      }
      break;
    case "temporary-use":
    case "short-term-lease":
      if (r.itemResults) {
        const tu: TemporaryUseFeeResult = {
          baseResult: {
            itemResults: r.itemResults as never,
            totalLandPrice: r.totalLandPrice ?? 0,
            totalChargeableGfa: r.totalChargeableGfa ?? 0,
            averageFloorLandPrice: r.averageFloorLandPrice ?? 0,
            validationMessages: [...r.validationMessages],
          },
          totalFee: r.totalFee ?? 0,
          note: r.note,
        };
        return formatTemporaryUseResult(
          tu,
          r.projectName ?? "—",
          r.kindLabel ?? "临时用地使用费",
        );
      }
      break;
    case "lease-rent":
      if (r.itemResults) {
        const lr: KeyIndustrialLeaseAnnualRentResult = {
          baseResult: {
            itemResults: r.itemResults as never,
            totalLandPrice: r.totalLandPrice ?? 0,
            totalChargeableGfa: r.totalChargeableGfa ?? 0,
            averageFloorLandPrice: r.averageFloorLandPrice ?? 0,
            validationMessages: [...r.validationMessages],
          },
          twentyYearLandPrice: r.twentyYearLandPrice ?? 0,
          annualRent: r.annualRent ?? 0,
          note: r.note,
          validationMessages: [...r.validationMessages],
        };
        return formatLeaseRentResult(lr);
      }
      break;
    case "capacity-increase":
      if (r.beforeResult && r.afterResult) {
        const ci: IndustrialCapacityIncreaseResult = {
          base: {
            beforeResult: r.beforeResult,
            afterResult: r.afterResult,
            difference:
              r.afterResult.totalLandPrice - r.beforeResult.totalLandPrice,
            premium: r.premium ?? 0,
          },
          itemResults: [],
          capacityIncreasePremium: r.capacityIncreasePremium ?? 0,
        };
        return formatCapacityIncreaseResult(ci);
      }
      break;
    case "batch":
      return [
        "# 批量测算汇总",
        "",
        `> 项目：${r.projectName ?? "—"}`,
        "",
        "| 项目 | 值 |",
        "|---|---:|",
        `| 宗地地价合计 (元) | ${r.totalLandPrice ?? 0} |`,
        `| 应补缴地价合计 (元) | ${r.premium ?? 0} |`,
        `| 校验消息数量 | ${r.validationMessages.length} |`,
      ].join("\n");
    default:
      break;
  }
  return "# 地价测算结果\n\n（缺少数据）\n";
}
